import { useState, useMemo, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  MenuItem,
  TablePagination,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import DashboardLayout from "@/components/DashboardLayout";
import * as materialApi from "@/api/material";
import * as materialherstellerApi from "@/api/materialhersteller";
import type { MaterialResponseDTO, MaterialherstellerResponseDTO } from "@/api/types";

interface SurgicalMaterial {
  id: number;
  manufacturerId: number;
  manufacturerName: string;
  name: string;
}

const SurgicalMaterials = () => {
  const [materials, setMaterials] = useState<SurgicalMaterial[]>([]);
  const [manufacturers, setManufacturers] = useState<MaterialherstellerResponseDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState<SurgicalMaterial | null>(null);
  const [formData, setFormData] = useState<{
    manufacturerId: number | string;
    name: string;
  }>({
    manufacturerId: "",
    name: "",
  });

  const [nameFilter, setNameFilter] = useState("");
  const [manufacturerFilter, setManufacturerFilter] = useState<number | "all">("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Map backend DTO to frontend interface
  const mapMaterialToFrontend = (dto: MaterialResponseDTO): SurgicalMaterial => ({
    id: dto.id,
    manufacturerId: dto.materialherstellerId,
    manufacturerName: dto.materialherstellerBezeichnung,
    name: dto.bezeichnung,
  });

  // Fetch materials and manufacturers on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const [materialsData, manufacturersData] = await Promise.all([
          materialApi.getAll(),
          materialherstellerApi.getAll(),
        ]);
        setMaterials(materialsData.map(mapMaterialToFrontend));
        setManufacturers(manufacturersData);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Fehler beim Laden der Daten");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const filteredMaterials = useMemo(() => {
    return materials.filter((material) => {
      const matchesName = material.name.toLowerCase().includes(nameFilter.toLowerCase());
      const matchesManufacturer =
        manufacturerFilter === "all" || material.manufacturerId === manufacturerFilter;
      return matchesName && matchesManufacturer;
    });
  }, [materials, nameFilter, manufacturerFilter]);

  const paginatedMaterials = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredMaterials.slice(start, start + rowsPerPage);
  }, [filteredMaterials, page, rowsPerPage]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleAddNew = () => {
    setEditingMaterial(null);
    setFormData({ manufacturerId: "", name: "" });
    setIsDialogOpen(true);
  };

  const handleEdit = (material: SurgicalMaterial) => {
    setEditingMaterial(material);
    setFormData({
      manufacturerId: material.manufacturerId,
      name: material.name,
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id: number) => {
    if (confirm("Möchten Sie dieses OP-Material wirklich löschen?")) {
      try {
        await materialApi.remove(id);
        setMaterials(materials.filter((m) => m.id !== id));
      } catch (err) {
        alert(err instanceof Error ? err.message : "Fehler beim Löschen");
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const payload = {
        bezeichnung: formData.name,
        materialherstellerId: Number(formData.manufacturerId),
      };

      if (editingMaterial) {
        // Update existing material
        const updated = await materialApi.update(editingMaterial.id, payload);
        setMaterials(
          materials.map((m) =>
            m.id === editingMaterial.id ? mapMaterialToFrontend(updated) : m
          )
        );
      } else {
        // Create new material
        const created = await materialApi.create(payload);
        setMaterials([...materials, mapMaterialToFrontend(created)]);
      }
      setIsDialogOpen(false);
    } catch (err) {
      alert(err instanceof Error ? err.message : "Fehler beim Speichern");
    }
  };

  return (
    <DashboardLayout>
      <Box sx={{ maxWidth: 1400, mx: "auto" }}>
        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Loading State */}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400 }}>
            <CircularProgress />
          </Box>
        ) : (
        <>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 4,
          }}
        >
          <Typography variant="h4" component="h1" fontWeight="bold">
            OP-Material
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddNew}
            size="large"
            sx={{ color: "white" }}
          >
            Neues OP-Material
          </Button>
        </Box>

        {/* Filter */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Filter
          </Typography>
          <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 2 }}>
            <TextField
              label="Bezeichnung suchen"
              placeholder="Suche eingeben..."
              value={nameFilter}
              onChange={(e) => { setNameFilter(e.target.value); setPage(0); }}
              fullWidth
            />
            <TextField
              select
              label="Hersteller"
              value={manufacturerFilter}
              onChange={(e) => { setManufacturerFilter(e.target.value === "all" ? "all" : Number(e.target.value)); setPage(0); }}
              fullWidth
            >
              <MenuItem value="all">Alle</MenuItem>
              {manufacturers.map((manufacturer) => (
                <MenuItem key={manufacturer.id} value={manufacturer.id}>
                  {manufacturer.bezeichnung}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </Paper>

        {/* Table */}
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Hersteller</strong></TableCell>
                  <TableCell><strong>Bezeichnung</strong></TableCell>
                  <TableCell align="right"><strong>Aktionen</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedMaterials.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 8 }}>
                      <Typography color="text.secondary">
                        {materials.length === 0
                          ? "Keine OP-Materialien vorhanden"
                          : "Keine OP-Materialien entsprechen den Filterkriterien"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedMaterials.map((material) => (
                    <TableRow key={material.id} hover>
                      <TableCell>{material.manufacturerName}</TableCell>
                      <TableCell>{material.name}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(material)}
                          color="primary"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(material.id)}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredMaterials.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage="Zeilen pro Seite:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} von ${count}`
            }
          />
        </Paper>
        </>
        )}
      </Box>

      {/* Dialog */}
      <Dialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingMaterial ? "OP-Material bearbeiten" : "Neues OP-Material hinzufügen"}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
              <TextField
                select
                label="Hersteller"
                value={formData.manufacturerId}
                onChange={(e) =>
                  setFormData({ ...formData, manufacturerId: Number(e.target.value) })
                }
                required
                fullWidth
              >
                {manufacturers.map((manufacturer) => (
                  <MenuItem key={manufacturer.id} value={manufacturer.id}>
                    {manufacturer.bezeichnung}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Bezeichnung"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                required
                fullWidth
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsDialogOpen(false)}>Abbrechen</Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={<SaveIcon />}
              sx={{ color: "white" }}
            >
              {editingMaterial ? "Speichern" : "Hinzufügen"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </DashboardLayout>
  );
};

export default SurgicalMaterials;
